import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import AnimatedSection from "../../components/base/AnimatedSection";
import ArticleContent from "./components/ArticleContent";
import { blogArticles } from "../../mocks/blog";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = blogArticles.find((a) => a.slug === slug);
  const related = blogArticles.filter((a) => a.slug !== slug).slice(0, 2);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col font-body">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="font-heading font-black text-brand-black text-4xl mb-4">404</p>
            <p className="font-body text-brand-gray-mid mb-6">Articulo no encontrado.</p>
            <button
              onClick={() => navigate("/blog")}
              className="bg-brand-red text-white font-body font-medium px-6 py-3 rounded-md cursor-pointer whitespace-nowrap"
            >
              Volver al blog
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col font-body bg-white">
      <Navbar />

      {/* Hero image */}
      <div className="relative w-full h-[420px] md:h-[520px] mt-0 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover"
          style={{ objectPosition: article.imagePosition }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-end px-6 pb-12">
          <div className="max-w-3xl mx-auto w-full">
            <span className="inline-block font-body text-xs font-medium text-white/80 tracking-widest uppercase mb-4 bg-brand-red px-3 py-1 rounded-sm">
              {article.category}
            </span>
            <h1 className="font-heading font-black text-white leading-tight"
              style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.8rem)" }}>
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-4">
              <span className="font-body text-white/70 text-sm">{article.date}</span>
              <span className="text-white/40">·</span>
              <span className="font-body text-white/70 text-sm">{article.readTime}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Article body */}
      <main className="flex-1 py-16 px-6">
        <div className="max-w-3xl mx-auto">

          {/* Back link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-body text-brand-gray-mid hover:text-brand-red text-sm transition-colors cursor-pointer mb-10"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Volver al blog
          </Link>

          <AnimatedSection>
            <ArticleContent sections={article.content} />
          </AnimatedSection>

          {/* CTA box */}
          <AnimatedSection delay={100}>
            <div
              className="mt-14 rounded-lg p-8 text-white"
              style={{ backgroundColor: "#E8150C" }}
            >
              <h3 className="font-heading font-bold text-xl mb-2 tracking-tight">
                Profesionaliza tu liga con Fair Play Global
              </h3>
              <p className="font-body text-white/85 text-sm leading-relaxed mb-5">
                Nos encargamos de toda la gestion: arbitros, fiscal, administracion y comunicacion. Tu solo te preocupas del deporte.
              </p>
              <Link
                to="/#contacto"
                className="inline-block bg-white text-brand-red font-body font-semibold text-sm px-6 py-3 rounded-md hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar informacion
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </main>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-14 px-6" style={{ backgroundColor: "#F4F4F4" }}>
          <div className="max-w-7xl mx-auto">
            <AnimatedSection>
              <h2 className="font-heading font-bold text-brand-black text-2xl tracking-tight mb-8">
                Otros articulos
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {related.map((rel, i) => (
                <AnimatedSection key={rel.slug} delay={i * 100}>
                  <Link
                    to={`/blog/${rel.slug}`}
                    className="group bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-brand-red transition-all duration-300 flex flex-col cursor-pointer"
                  >
                    <div className="w-full h-44 overflow-hidden">
                      <img
                        src={rel.image}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        style={{ objectPosition: rel.imagePosition }}
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <span className="font-body text-xs font-medium text-brand-red tracking-widest uppercase mb-2">
                        {rel.category}
                      </span>
                      <h3 className="font-heading font-bold text-brand-black text-base leading-tight mb-3 tracking-tight flex-1">
                        {rel.title}
                      </h3>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="font-body text-brand-gray-mid text-xs">{rel.readTime}</span>
                        <span className="font-body text-brand-red text-sm font-medium group-hover:underline whitespace-nowrap">
                          Leer mas →
                        </span>
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
